import Nav from "@/components/Nav";
import { Link } from "react-router-dom";
import { Music, Wand2, Film, Sparkles, Zap, ImagePlus } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1499415479124-43c32433a620?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBuZW9uJTIwbXVzaWMlMjB2aWRlbyUyMHByb2R1Y3Rpb258ZW58MHx8fHwxNzg3Njc1NDEyfDA&ixlib=rb-4.1.0&q=85";

const STUDIO_IMG =
  "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMHN0dWRpbyUyMGVkaXRpbmclMjB0aW1lbGluZXxlbnwwfHx8fDE3ODc2NzU0MTJ8MA&ixlib=rb-4.1.0&q=85";

const VIBE_IMG =
  "https://images.pexels.com/photos/18569238/pexels-photo-18569238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Marquee = ({ items }) => (
  <div className="overflow-hidden border-y border-[#222] py-3">
    <div className="marquee font-display text-4xl md:text-6xl">
      {[...items, ...items].map((t, i) => (
        <span key={i} className="mx-8 outline-text whitespace-nowrap">
          {t} <span className="mx-4 text-[#FF003C]">•</span>
        </span>
      ))}
    </div>
  </div>
);

export default function Landing() {
  return (
    <div>
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#222]">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.4), rgba(5,5,5,0.95)), url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 pb-24 pt-16 md:pt-24">
          <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#FFE800]">
            <Zap size={14} /> Sora 2 Engine // Claude Storyboards
          </div>
          <h1 className="font-display text-[16vw] leading-[0.85] tracking-tighter md:text-[10vw]">
            YOUR SONG.
            <br />
            <span className="text-[#FF003C]">A MUSIC VIDEO</span>
            <br />
            IN <span className="text-[#FFE800]">MINUTES.</span>
          </h1>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            <p className="max-w-md text-lg text-[#c8c8c8] md:col-span-2">
              Upload your track or generate lyrics from scratch. Drop in reference photos. Our AI
              director builds a shot-by-shot storyboard and renders every scene with{" "}
              <span className="text-white">Sora&nbsp;2</span>. Full-length videos, cinematic quality,
              zero editing skills required.&nbsp;
            </p>
            <div className="flex flex-wrap items-start gap-3 md:justify-end">
              <Link to="/register" data-testid="hero-cta-primary" className="btn-brutal">
                <Wand2 size={18} /> Make a video
              </Link>
              <Link to="/pricing" data-testid="hero-cta-secondary" className="btn-brutal secondary">
                $15.99/MO PRO
              </Link>
            </div>
          </div>

          {/* stats strip */}
          <div className="mt-14 grid grid-cols-2 border border-[#222] md:grid-cols-4">
            {[
              ["01", "Storyboard", "Claude Sonnet 5"],
              ["02", "Render", "Sora 2"],
              ["03", "Audio", "Upload or AI"],
              ["04", "Visuals", "Your photos"],
            ].map(([n, t, s]) => (
              <div key={n} className="border-b border-r border-[#222] p-6 last:border-r-0 md:border-b-0">
                <div className="font-mono text-xs text-[#808080]">// {n}</div>
                <div className="font-display text-4xl">{t}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-[#FFE800]">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={["SORA 2 ENGINE", "CINEMATIC AI", "YOUR STORY", "MADE LOUD", "FULL LENGTH"]} />

      {/* HOW IT WORKS - brutalist tetris grid */}
      <section className="mx-auto max-w-[1400px] px-6 py-24">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-display text-6xl uppercase leading-none tracking-tight md:text-7xl">
            Four Steps.
            <br />
            <span className="text-[#00F0FF]">One Video.</span>
          </h2>
          <p className="max-w-md text-[#c8c8c8]">
            No timelines to master. No prompts to overthink. Feed the machine, watch it cook.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-0 border border-[#222] md:grid-cols-6">
          <div className="border-b border-r border-[#222] p-8 md:col-span-3 md:row-span-2">
            <div className="mb-4 flex items-center gap-3">
              <Music className="text-[#FF003C]" size={20} />
              <span className="font-mono text-xs uppercase tracking-widest">Step 01</span>
            </div>
            <div className="font-display text-5xl leading-none">Drop Your Track</div>
            <p className="mt-4 max-w-md text-[#c8c8c8]">
              Upload an MP3 or WAV, or let Claude Sonnet 5 write original lyrics for any theme, genre,
              or mood you throw at it.
            </p>
            <img
              src={VIBE_IMG}
              alt="creative vibe"
              className="mt-6 w-full border border-[#222] object-cover"
              style={{ height: 260 }}
            />
          </div>

          <div className="border-b border-r border-[#222] p-8 md:col-span-3">
            <div className="mb-4 flex items-center gap-3">
              <ImagePlus className="text-[#FFE800]" size={20} />
              <span className="font-mono text-xs uppercase tracking-widest">Step 02</span>
            </div>
            <div className="font-display text-5xl leading-none">Add Your Photos</div>
            <p className="mt-4 text-[#c8c8c8]">
              Drop reference images — band shots, mood boards, your own face. The director folds them
              into the visual direction.
            </p>
          </div>

          <div className="border-b border-[#222] p-8 md:col-span-3">
            <div className="mb-4 flex items-center gap-3">
              <Sparkles className="text-[#00F0FF]" size={20} />
              <span className="font-mono text-xs uppercase tracking-widest">Step 03</span>
            </div>
            <div className="font-display text-5xl leading-none">AI Storyboard</div>
            <p className="mt-4 text-[#c8c8c8]">
              Claude Sonnet 5 breaks your song into cinematic scenes — camera moves, lighting, subject,
              mood. Edit anything you want.
            </p>
          </div>

          <div className="p-8 md:col-span-6">
            <div className="mb-4 flex items-center gap-3">
              <Film className="text-[#FF003C]" size={20} />
              <span className="font-mono text-xs uppercase tracking-widest">Step 04</span>
            </div>
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <div className="font-display text-5xl leading-none">Sora 2 Renders It</div>
                <p className="mt-4 max-w-md text-[#c8c8c8]">
                  Each scene is generated in high fidelity by OpenAI Sora 2. Watch your song become a
                  full-length music video, delivered ready to share.
                </p>
                <Link to="/studio" data-testid="how-it-works-cta" className="btn-brutal mt-8 inline-flex">
                  <Zap size={16} /> Launch Studio
                </Link>
              </div>
              <img
                src={STUDIO_IMG}
                alt="studio"
                className="w-full border border-[#222] object-cover"
                style={{ height: 320 }}
              />
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["FULL LENGTH VIDEOS", "$15.99 A MONTH", "UNLIMITED SCENES", "GO LOUD"]} />

      {/* FOOTER */}
      <footer className="mx-auto max-w-[1400px] border-t border-[#222] px-6 py-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="font-display text-5xl">VOLT.MV</div>
            <p className="mt-2 max-w-md text-sm text-[#808080]">
              An AI music video studio for artists who do not wait around for a director.
            </p>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#808080]">
            © {new Date().getFullYear()} VOLT.MV // POWERED BY SORA 2
          </div>
        </div>
      </footer>
    </div>
  );
}
